import type { Collection } from '@tanstack/db';
import { registerDebugNamespace } from '@/lib/debug/registerDebugNamespace';
import type { ProfileResponse } from '@/lib/validation/api';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import { fromProfileResponse } from '@/lib/view-models/user-preview';

import {
  extractProfileIdFromKey,
  extractUsernameFromKey,
  isUsernameCacheKey,
  normalizeCacheKey,
  resolveUserPreviewCacheKey,
  resolveUserPreviewCacheKeys,
  resolveUserPreviewCacheLookup,
  type UserPreviewCacheUsernameIndex,
} from './helpers/user-preview-cache.keys';
import {
  isRecordExpired,
  normalizeDraft,
  type UserPreviewCacheRecordDraft,
} from './helpers/user-preview-cache.normalize';
import { hasMeaningfulPreview } from './helpers/user-preview-cache.utils';
import { assignStorageRecord, deserializeCacheRecord, serializeCacheRecord } from './user-preview-cache.storage';
import {
  decodePayload,
  decodeUserPreviewPayload,
  type UserPreviewCacheEntry,
  type UserPreviewCachePatch,
  type UserPreviewCacheRecord,
  UserPreviewCacheRecordSchema,
  type UserPreviewCacheSnapshot,
  type UserPreviewCacheStorageRecord,
} from './user-preview-cache.types';

type StoreWriter = {
  getState: () => {
    entries: Map<string, UserPreviewCacheEntry>;
    upsert: (primaryKey: string, entry: UserPreviewCacheEntry) => void;
    remove: (primaryKey: string) => void;
    reset: () => void;
  };
};

type UserPreviewCacheCollection = Pick<
  Collection<UserPreviewCacheStorageRecord, string>,
  'get' | 'has' | 'entries' | 'keys' | 'size' | 'delete' | 'insert' | 'update'
>;

type UserPreviewCacheDeps = {
  collection: UserPreviewCacheCollection;
  usernameIndex: UserPreviewCacheUsernameIndex;
  acceptCollectionTransaction: (tx: unknown) => void;
  cacheTtlMs: number;
  debugNamespace: string;
  store: StoreWriter;
  now?: () => number;
  debugEnabled?: boolean;
};

export function createUserPreviewCacheService(deps: UserPreviewCacheDeps) {
  const {
    collection,
    usernameIndex,
    acceptCollectionTransaction,
    cacheTtlMs,
    debugNamespace,
    store,
    now = Date.now,
    debugEnabled = true,
  } = deps;

  const mapRecordToEntry = (record: UserPreviewCacheRecord | undefined): UserPreviewCacheEntry | undefined => {
    if (!record) {
      return;
    }
    return {
      error: record.error,
      detail: record.detail,
      fetchedAt: record.fetchedAt,
      aliases: record.aliases,
      ...(record.preview ? { preview: record.preview } : {}),
      ...(record.profile ? { profile: record.profile } : {}),
      ...(record.usernameKey ? { usernameKey: record.usernameKey } : {}),
    } satisfies UserPreviewCacheEntry;
  };

  const syncStoreFromRecord = (primaryKey: string, record?: UserPreviewCacheRecord) => {
    const { upsert, remove } = store.getState();
    const entry = mapRecordToEntry(record);
    if (!entry) {
      remove(primaryKey);
      return;
    }

    upsert(primaryKey, entry);
  };

  const findUsernamePrimaryKeyInCollection = (usernameKey: string): string | undefined => {
    try {
      if (usernameIndex.isReady) {
        const matches = usernameIndex.equalityLookup(usernameKey);
        const first = matches.values().next();
        if (!first.done) {
          const value = first.value;
          return typeof value === 'string' ? value : undefined;
        }
      }
    } catch {
      // Index may not be ready yet—fall back to scan.
    }

    for (const [key, record] of collection.entries()) {
      if (record.usernameKey === usernameKey) {
        return String(key);
      }
    }

    for (const [key, entry] of store.getState().entries.entries()) {
      if (entry.usernameKey === usernameKey) {
        return key;
      }
    }

    return;
  };

  const resolveExistingPrimaryKey = (cacheKey: string | null): string | undefined => {
    const normalized = normalizeCacheKey(cacheKey);
    if (!normalized) {
      return;
    }

    const profileId = extractProfileIdFromKey(normalized);

    if (collection.has(normalized)) {
      return normalized;
    }

    if (isUsernameCacheKey(normalized)) {
      const usernameKey = extractUsernameFromKey(normalized);
      if (usernameKey) {
        const resolved = findUsernamePrimaryKeyInCollection(usernameKey);
        if (resolved) {
          return resolved;
        }
      }
    }

    for (const [key, stored] of collection.entries()) {
      const inflated = deserializeCacheRecord(stored);
      const aliases = inflated?.aliases ?? stored.aliases;
      if (aliases.includes(normalized)) {
        return String(key);
      }
      if (profileId && inflated && (inflated.profile?.id === profileId || inflated.preview?.id === profileId)) {
        return String(key);
      }
    }

    const storeEntries = store.getState().entries;
    if (storeEntries.has(normalized)) {
      return normalized;
    }
    for (const [key, entry] of storeEntries.entries()) {
      if (entry.aliases.includes(normalized)) {
        return key;
      }
      if (profileId && (entry.profile?.id === profileId || entry.preview?.id === profileId)) {
        return key;
      }
    }

    return;
  };

  const assignOptionalString = <K extends string>(
    target: Record<string, unknown> & Partial<Record<K, string | undefined | null>>,
    key: K,
    value: string | undefined | null
  ) => {
    if (value === undefined || value === null || value === '') {
      Reflect.deleteProperty(target, key);
      return;
    }
    (target as Partial<Record<K, string | null | undefined>>)[key] = value;
  };

  const publishDebugSnapshot = (): void => {
    if (!debugEnabled || globalThis.window === undefined) {
      return;
    }
    const entries = [...collection.entries()].map(([key, entry]) => ({
      key,
      detail: entry.detail,
      error: entry.error,
      fetchedAt: entry.fetchedAt,
      hasProfile: Boolean(entry.profile),
      hasPreview: Boolean(entry.preview),
      aliases: entry.aliases.length,
    }));

    registerDebugNamespace(debugNamespace, {
      size: collection.size,
      entries,
    });
  };

  const writeRecord = (
    cacheKey: string | null,
    builder: (previous: UserPreviewCacheRecord | undefined) => UserPreviewCacheRecordDraft | undefined
  ): UserPreviewCacheEntry | undefined => {
    const normalizedCacheKey = normalizeCacheKey(cacheKey);
    if (!normalizedCacheKey) {
      return;
    }

    const resolvedKey = resolveExistingPrimaryKey(normalizedCacheKey);
    let previousStored: UserPreviewCacheStorageRecord | undefined;
    if (resolvedKey) {
      previousStored = collection.get(resolvedKey);
    }
    const previous = deserializeCacheRecord(previousStored);
    const draft = builder(previous);

    if (draft === undefined) {
      if (resolvedKey && collection.has(resolvedKey)) {
        const tx = collection.delete(resolvedKey);
        acceptCollectionTransaction(tx);
        syncStoreFromRecord(resolvedKey);
        publishDebugSnapshot();
      }
      return;
    }

    const normalized = normalizeDraft({
      draft,
      previous,
      cacheKey: normalizedCacheKey,
      now,
    });

    if (!normalized) {
      if (resolvedKey && collection.has(resolvedKey)) {
        const tx = collection.delete(resolvedKey);
        acceptCollectionTransaction(tx);
        syncStoreFromRecord(resolvedKey);
        publishDebugSnapshot();
      }
      return;
    }

    const targetKey = normalized.primaryCacheKey;
    const serialized = serializeCacheRecord(normalized);

    if (resolvedKey && resolvedKey !== targetKey && collection.has(resolvedKey)) {
      const deleteTx = collection.delete(resolvedKey);
      acceptCollectionTransaction(deleteTx);
      syncStoreFromRecord(resolvedKey);
    }

    const mutationTx: unknown = collection.has(targetKey)
      ? collection.update(targetKey, (draftRecord) => {
          assignStorageRecord(draftRecord, serialized);
        })
      : collection.insert(serialized);

    acceptCollectionTransaction(mutationTx);

    const stored = deserializeCacheRecord(collection.get(targetKey)) ?? normalized;
    publishDebugSnapshot();
    syncStoreFromRecord(targetKey, stored);
    return mapRecordToEntry(stored);
  };

  const safeGet = (cacheKey: string | null | undefined): UserPreviewCacheRecord | undefined => {
    const normalizedCacheKey = normalizeCacheKey(cacheKey);
    if (!normalizedCacheKey) {
      return;
    }

    const resolvedKey = resolveExistingPrimaryKey(normalizedCacheKey) ?? normalizedCacheKey;
    const record = deserializeCacheRecord(collection.get(resolvedKey));
    if (!record) {
      return;
    }

    if (isRecordExpired(record, now, cacheTtlMs)) {
      try {
        const tx = collection.delete(resolvedKey);
        acceptCollectionTransaction(tx);
        syncStoreFromRecord(resolvedKey);
      } catch {
        // Ignore deletion errors.
      }
      publishDebugSnapshot();
      return;
    }

    return record;
  };

  const getUserPreviewCacheEntry = (cacheKey: string | null): UserPreviewCacheEntry | undefined =>
    mapRecordToEntry(safeGet(cacheKey));

  const setUserPreviewCacheEntry = (
    cacheKey: string | null,
    entry: Omit<UserPreviewCacheEntry, 'fetchedAt'> & { fetchedAt?: number }
  ): UserPreviewCacheEntry | undefined => {
    if (!cacheKey) {
      return;
    }
    return writeRecord(cacheKey, () => {
      const draft: UserPreviewCacheRecordDraft = {
        error: entry.error ?? false,
        detail: entry.detail,
        fetchedAt: entry.fetchedAt ?? now(),
      };
      if (Object.hasOwn(entry, 'preview')) {
        draft.preview = entry.preview ?? null;
      }
      if (Object.hasOwn(entry, 'profile')) {
        draft.profile = entry.profile ?? null;
      }
      if (Object.hasOwn(entry, 'usernameKey')) {
        assignOptionalString(draft, 'username', entry.usernameKey);
      }
      return draft;
    });
  };

  const upsertUserPreviewCacheEntry = (
    cacheKey: string | null,
    patch: UserPreviewCachePatch
  ): UserPreviewCacheEntry | undefined => {
    if (!cacheKey) {
      return;
    }

    return writeRecord(cacheKey, (previous) => {
      const draft: UserPreviewCacheRecordDraft = {
        error: patch.error ?? previous?.error ?? false,
        detail: patch.detail ?? (patch.profile ? 'full' : undefined) ?? previous?.detail ?? 'partial',
        fetchedAt: patch.fetchedAt ?? now(),
      };
      if (Object.hasOwn(patch, 'preview')) {
        draft.preview = patch.preview ?? null;
      }
      if (Object.hasOwn(patch, 'profile')) {
        draft.profile = patch.profile ?? null;
      }
      if (Object.hasOwn(patch, 'profileId')) {
        assignOptionalString(draft, 'profileId', patch.profileId ?? undefined);
      }
      if (Object.hasOwn(patch, 'username')) {
        assignOptionalString(draft, 'username', patch.username ?? undefined);
      }
      return draft;
    });
  };

  const invalidateUserPreviewCacheEntry = (cacheKey: string | null): void => {
    if (!cacheKey) {
      return;
    }

    const normalized = normalizeCacheKey(cacheKey);
    if (!normalized) {
      return;
    }

    const resolvedKey = resolveExistingPrimaryKey(normalized) ?? normalized;
    if (!collection.has(resolvedKey)) {
      return;
    }

    const deleteTx = collection.delete(resolvedKey);
    acceptCollectionTransaction(deleteTx);
    publishDebugSnapshot();
    syncStoreFromRecord(resolvedKey);
  };

  const resetUserPreviewCacheForTests = (): void => {
    for (const key of collection.keys()) {
      const tx = collection.delete(key);
      acceptCollectionTransaction(tx);
    }
    publishDebugSnapshot();
    store.getState().reset();
  };

  const primeUserPreviewCacheFromProfile = (profile: ProfileResponse): void => {
    const normalizedProfile = decodePayload<ProfileResponse>(profile);
    if (!normalizedProfile) {
      return;
    }

    const keys = resolveUserPreviewCacheKeys({ profileId: normalizedProfile.id, username: normalizedProfile.username });
    if (keys.length === 0) {
      return;
    }

    const preview = fromProfileResponse(normalizedProfile);

    for (const key of keys) {
      upsertUserPreviewCacheEntry(key, {
        profile: normalizedProfile,
        preview,
        error: false,
        detail: 'full',
        profileId: normalizedProfile.id,
        username: normalizedProfile.username,
      });
    }
  };

  const primeUserPreviewCacheFromPreview = (preview: UserPreviewVM): void => {
    if (!hasMeaningfulPreview(preview)) {
      return;
    }
    const keys = resolveUserPreviewCacheKeys({ profileId: preview.id, username: preview.username });
    if (keys.length === 0) {
      return;
    }

    for (const key of keys) {
      upsertUserPreviewCacheEntry(key, {
        preview,
        error: false,
        detail: 'partial',
        profileId: preview.id,
        username: preview.username,
      });
    }
  };

  const dehydrateUserPreviewCache = (): UserPreviewCacheSnapshot => {
    const entries: UserPreviewCacheRecord[] = [];
    for (const [, rawRecord] of collection.entries()) {
      const record = deserializeCacheRecord(rawRecord);
      if (record) {
        entries.push(structuredClone(record));
      }
    }
    return {
      capturedAt: now(),
      entries,
    };
  };

  const hydrateUserPreviewCache = (snapshot: UserPreviewCacheSnapshot | null | undefined): void => {
    if (!(snapshot && Array.isArray(snapshot.entries)) || snapshot.entries.length === 0) {
      return;
    }

    const referenceTime = snapshot.capturedAt ?? now();

    for (const raw of snapshot.entries) {
      const parsed = UserPreviewCacheRecordSchema.safeParse(raw);
      if (!parsed.success) {
        continue;
      }

      const { preview: rawPreview, profile: rawProfile, profileId: rawProfileId, usernameKey, ...rest } = parsed.data;
      const preview = decodeUserPreviewPayload(rawPreview);
      const profile = decodePayload<ProfileResponse>(rawProfile);
      const profileId = rawProfileId ?? undefined;
      const normalizedUsernameKey = usernameKey ?? undefined;
      const record: UserPreviewCacheRecord = {
        ...rest,
        ...(profileId ? { profileId } : {}),
        ...(normalizedUsernameKey ? { usernameKey: normalizedUsernameKey } : {}),
        ...(preview ? { preview } : {}),
        ...(profile ? { profile } : {}),
      };

      if (referenceTime - record.fetchedAt > cacheTtlMs) {
        continue;
      }

      if (collection.has(record.primaryCacheKey)) {
        continue;
      }

      const insertTx = collection.insert(serializeCacheRecord(record));
      acceptCollectionTransaction(insertTx);
      syncStoreFromRecord(record.primaryCacheKey, record);
    }

    publishDebugSnapshot();
  };

  const inflateUserPreviewCacheRecord = (
    record: UserPreviewCacheStorageRecord | null | undefined
  ): UserPreviewCacheRecord | undefined => deserializeCacheRecord(record);

  const bootstrapStoreFromCollection = () => {
    for (const [key, record] of collection.entries()) {
      const inflated = deserializeCacheRecord(record);
      if (inflated) {
        syncStoreFromRecord(String(key), inflated);
      }
    }
  };

  return {
    getUserPreviewCacheEntry,
    setUserPreviewCacheEntry,
    upsertUserPreviewCacheEntry,
    invalidateUserPreviewCacheEntry,
    resetUserPreviewCacheForTests,
    primeUserPreviewCacheFromProfile,
    primeUserPreviewCacheFromPreview,
    dehydrateUserPreviewCache,
    hydrateUserPreviewCache,
    inflateUserPreviewCacheRecord,
    resolveUserPreviewCacheKeys,
    resolveUserPreviewCacheKey,
    resolveUserPreviewCacheLookup,
    hasMeaningfulPreview,
    bootstrapStoreFromCollection,
    publishDebugSnapshot,
    safeGet,
    resolveExistingPrimaryKey,
  };
}
